const portraitMediaQuery = window.matchMedia('(orientation: portrait)');
const lazyLoadMargin = 200;
const videoSourceUpdateDelay = 150;
const videoResumeDelay = 250;

function pickVideoUris(videos) {
    if (portraitMediaQuery.matches && videos.portraitUris1920 && videos.portraitUris1920.length) {
        return {
            key: 'portrait',
            uris: videos.portraitUris1920
        };
    }

    if ((window.innerWidth <= 1366 || !(videos.uris1920 && videos.uris1920.length)) && videos.uris1366 && videos.uris1366.length) {
        return {
            key: '1366',
            uris: videos.uris1366
        };
    }

    return {
        key: '1920',
        uris: videos.uris1920 || []
    };
}

function normalizeVideoUri(uri) {
    try {
        return new URL(uri, document.baseURI).href;
    } catch (error) {
        return uri;
    }
}

function currentVideoSourceMatchesPick(el, picks) {
    const currentSrc = el.currentSrc ? normalizeVideoUri(el.currentSrc) : '';

    return !!currentSrc && picks.uris.some((uri) => normalizeVideoUri(uri) === currentSrc);
}

function renderVideoSources(el, videos) {
    if (document.visibilityState === 'hidden') {
        return;
    }

    const picks = pickVideoUris(videos);

    if (el.dataset.videoSourceKey === picks.key || !picks.uris.length) {
        return;
    }

    el.dataset.videoSourceKey = picks.key;
    el.innerHTML = picks.uris
        .map((uri) => `<source src="${uri}" type="${uri.includes('.mp4') ? 'video/mp4' : 'video/mp4'}">`)
        .join('');
    el.load();

    if (el.dataset.videoAutoplay === 'true') {
        playAutoplayVideo(el);
    }
}

export function setAutoplayState(el, state) {
    const fallbackPoster = el.closest('.video__inner')?.querySelector('.video__fallback-poster');

    el.dataset.videoAutoplayState = state;
    el.style.visibility = state === 'blocked' && !fallbackPoster ? 'hidden' : '';

    if (fallbackPoster) {
        fallbackPoster.classList.toggle('invisible', state !== 'blocked');
    }
}

export function isAutoplayBlockedError(error) {
    return error && error.name === 'NotAllowedError';
}

export function playAutoplayVideo(el) {
    if (document.visibilityState === 'hidden' || el.dataset.videoAutoplay !== 'true') {
        return;
    }

    const requestId = String((Number(el.dataset.videoAutoplayRequestId) || 0) + 1);
    el.dataset.videoAutoplayRequestId = requestId;
    setAutoplayState(el, 'pending');

    el.autoplay = true;
    el.muted = true;
    el.setAttribute('muted', '');
    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');

    const playPromise = el.play();

    if (!playPromise || typeof playPromise.then !== 'function') {
        setAutoplayState(el, el.paused ? 'pending' : 'playing');
        return;
    }

    playPromise
        .then(() => {
            if (el.dataset.videoAutoplayRequestId === requestId) {
                setAutoplayState(el, 'playing');
            }
        })
        .catch((error) => {
            if (el.dataset.videoAutoplayRequestId !== requestId) {
                return;
            }

            if (isAutoplayBlockedError(error)) {
                setAutoplayState(el, 'blocked');
                return;
            }

            setAutoplayState(el, el.paused ? 'idle' : 'playing');
        });
}

function loadVideo(el, videos) {
    if (el.dataset.videoLoaded) {
        return;
    }

    el.dataset.videoLoaded = 'true';
    renderVideoSources(el, videos);
}

function isNearViewport(el) {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
        rect.top <= viewportHeight + lazyLoadMargin &&
        rect.bottom >= lazyLoadMargin * -1 &&
        rect.left <= viewportWidth + lazyLoadMargin &&
        rect.right >= lazyLoadMargin * -1
    );
}

document.querySelectorAll('.video__player').forEach(function (el) {
    const data = el.getAttribute('data-video-uris');
    if (!data) {
        return;
    }

    const videos = JSON.parse(data);
    const updateSources = () => renderVideoSources(el, videos);
    const load = () => loadVideo(el, videos);
    const isEager = el.dataset.videoLoading === 'eager';
    const observerTarget = el.closest('.video') || el;
    let loadCheckFrame = null;
    let sourceUpdateTimer = null;
    let resumeTimer = null;

    el.addEventListener('playing', () => {
        setAutoplayState(el, 'playing');
    });
    el.addEventListener('pause', () => {
        setAutoplayState(el, 'idle');
    });
    el.addEventListener('ended', () => {
        setAutoplayState(el, 'idle');
    });
    el.addEventListener('error', () => {
        setAutoplayState(el, 'idle');
    });

    const stopWatching = () => {
        window.removeEventListener('scroll', scheduleLoadCheck);
        window.removeEventListener('resize', scheduleLoadCheck);
        if (loadCheckFrame) {
            cancelAnimationFrame(loadCheckFrame);
        }
    };

    const loadIfNearViewport = () => {
        loadCheckFrame = null;
        if (!isNearViewport(observerTarget)) {
            return;
        }

        stopWatching();
        load();
    };

    function scheduleLoadCheck() {
        if (el.dataset.videoLoaded || loadCheckFrame) {
            return;
        }

        loadCheckFrame = requestAnimationFrame(loadIfNearViewport);
    }

    function scheduleSourceUpdate() {
        if (!el.dataset.videoLoaded || document.visibilityState === 'hidden') {
            return;
        }

        if (sourceUpdateTimer) {
            window.clearTimeout(sourceUpdateTimer);
        }

        sourceUpdateTimer = window.setTimeout(() => {
            sourceUpdateTimer = null;
            updateSources();
        }, videoSourceUpdateDelay);
    }

    function resumeAutoplay() {
        if (!el.dataset.videoLoaded || document.visibilityState === 'hidden') {
            return;
        }

        if (resumeTimer) {
            window.clearTimeout(resumeTimer);
        }

        resumeTimer = window.setTimeout(() => {
            resumeTimer = null;
            updateSources();
            playAutoplayVideo(el);
        }, videoResumeDelay);
    }

    if (isEager) {
        el.dataset.videoLoaded = 'true';
        const picks = pickVideoUris(videos);

        // Safari can keep an eager fallback source even when the portrait source should win.
        if (el.querySelector('source[src]') && currentVideoSourceMatchesPick(el, picks)) {
            el.dataset.videoSourceKey = picks.key;
            playAutoplayVideo(el);
            return;
        }

        updateSources();
    } else {
        window.addEventListener('scroll', scheduleLoadCheck, { passive: true });
        window.addEventListener('resize', scheduleLoadCheck);
        scheduleLoadCheck();
    }

    if (portraitMediaQuery.addEventListener) {
        portraitMediaQuery.addEventListener('change', () => {
            scheduleSourceUpdate();
        });
    } else {
        portraitMediaQuery.addListener(() => {
            scheduleSourceUpdate();
        });
    }
    window.addEventListener('resize', scheduleSourceUpdate);
    window.addEventListener('pageshow', resumeAutoplay);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            resumeAutoplay();
        }
    });
});
